<script>
    import moment from "moment";
    import { onMount } from "svelte";
    import { writable } from "svelte/store";
    import API from "../../API/API";
    import StringHelper from "../../Helpers/StringHelper";
    import VesselProperty from "../../Models/Enums/VesselProperty";
    import Vessel from "../../Models/Vessel";
    import Flag from '../Flag.svelte';
    import Spinner from "../Spinner/Spinner.svelte";
    import SpinnerSize from "../Spinner/SpinnerSize";

    export let vessel = writable(null);

    let tabLoading = writable(false);

    $: {
        if ($vessel) {
            const useMMSI = $vessel.mmsi;
            if (!$vessel.enhancedData) {
                $tabLoading = true;
                API.request("vessels_v2?mmsi=" + useMMSI)
                    .then((response) => {
                        if (response && response.vessels && response.vessels.nodes && response.vessels.nodes.length > 0) {
                            const vesselData = response.vessels.nodes[0];
                            if (vesselData.characteristics !== null) {
                                $vessel.enhancedData = vesselData;
                            }
                        };
                    })
                    .catch(() => {
                    })
                    .finally(() => {
                        $tabLoading = false;
                    })
            }
        }
    };
</script>

{#if $tabLoading}
    <p style="text-align:center;margin:0px;overflow:auto;height:48px;padding-top:16px;">
        <Spinner size={SpinnerSize.SMALL} absolute={false} inline={true} />
    </p>
{:else}
    {#if $vessel !== null}
        {#if $vessel.previousVersions.length > 0}
            <p class='category'>Other Assignments:</p>
            <ul>
                {#each $vessel.previousVersionDiffs as diff}
                    <li>
                        <div>
                            {#if moment(diff[VesselProperty.TIMESTAMP]) > moment($vessel.timestamp)}
                                <p><u>Latest</u></p>
                            {:else}
                                <p><u>{moment(diff[VesselProperty.TIMESTAMP]).format("MMM Do YYYY")}<br/></u>{moment(diff[VesselProperty.TIMESTAMP]).format("hh:mm:ssa [(UTC)]")}</p>
                            {/if}
                        </div>
                        <div>
                            {#each Object.keys(diff) as key, index}
                                {#if key === "flag"}
                                    <p><b>{StringHelper.ucfirst(key)}:</b> <Flag cc={diff[key]}/> {Vessel.formatUndefined(diff[key])}</p>
                                {:else if key !== "timestamp"}
                                    <p><b>{StringHelper.ucfirst(key)}:</b> {diff[key]}</p>
                                {/if}
                            {/each}
                        </div>
                    </li>
                {/each}
            </ul>
        {/if}

        {#if $vessel.enhancedData === null}
            <p>No enhanced data found for this vessel.</p>
        {:else}
            <p class='category'>Capacity:</p>
            <ul>
                {#if $vessel.enhancedData.characteristics.extended.capacity.deadweight !== null}
                <li>
                    <div>Deadweight:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.capacity.deadweight}</div>
                </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.tpcmi !== null}
            <li>
                <div>TPCMI:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.tpcmi}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.netTonnage !== null}
            <li>
                <div>Net Tonnage:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.netTonnage}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.grossTonnage !== null}
            <li>
                <div>Gross Tonnage:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.grossTonnage}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.displacement !== null}
            <li>
                <div>Displacement:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.displacement}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.liquidCubic98Percent !== null}
            <li>
                <div>Liquid Cubic 98 Percent:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.liquidCubic98Percent}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.grainCubicCapacity !== null}
            <li>
                <div>Grain Cubic Capacity:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.grainCubicCapacity}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.teu !== null}
            <li>
                <div>TEU:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.teu}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.holdCount !== null}
            <li>
                <div>Hold Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.holdCount}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.holdDimensions !== null}
            <li>
                <div>Hold Dimensions:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.holdDimensions}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.hatchCount !== null}
            <li>
                <div>Hatch Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.hatchCount}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.hatchDimensions !== null}
            <li>
                <div>Hatch Dimensions:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.hatchDimensions}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.feu !== null}
            <li>
                <div>FEU:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.feu}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.teuSurplus !== null}
            <li>
                <div>TEU Surplus:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.teuSurplus}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.teu14t !== null}
            <li>
                <div>TEU 14t:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.teu14t}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.laneMeters !== null}
            <li>
                <div>Lane Meters:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.laneMeters}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.cars !== null}
            <li>
                <div>Cars:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.cars}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.passengers !== null}
            <li>
                <div>Passengers:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.passengers}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.capacity.reeferCubic !== null}
            <li>
                <div>Reefer Cubic:</div>
                <div>{$vessel.enhancedData.characteristics.extended.capacity.reeferCubic}</div>
            </li>
            {/if}
            </ul>

            <p class='category'>Design:</p>
            <ul>
                {#if $vessel.enhancedData.characteristics.extended.design.isCoated !== null}
                <li>
                    <div>Is Coated:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.isCoated}</div>
                </li>
            {/if}
                {#if $vessel.enhancedData.characteristics.extended.design.isGearless !== null}
                <li>
                    <div>Is Gearless:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.isGearless}</div>
                </li>
            {/if}
                {#if $vessel.enhancedData.characteristics.extended.design.isSelfUnloading !== null}
                <li>
                    <div>Is Self Unloading:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.isSelfUnloading}</div>
                </li>
            {/if}            
                {#if $vessel.enhancedData.characteristics.extended.design.gearDisplay !== null}
                <li>
                    <div>Gear Display:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.gearDisplay}</div>
                </li>
            {/if}            
                {#if $vessel.enhancedData.characteristics.extended.design.gearMaxSwl !== null}
                <li>
                    <div>Gear Max SWL:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.gearMaxSwl}</div>
                </li>
            {/if}            
                {#if $vessel.enhancedData.characteristics.extended.design.reeferPointCount !== null}
                <li>
                    <div>Reefer Point Count:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.reeferPointCount}</div>
                </li>
            {/if}            
                {#if $vessel.enhancedData.characteristics.extended.design.hullTypeCode !== null}
                <li>
                    <div>Hull Type Code:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.design.hullTypeCode}</div>
                </li>
            {/if}            
            </ul>

            <p class='category'>Dimensions:</p>
            <ul>
            {#if $vessel.enhancedData.characteristics.extended.dimensions.draught !== null}
                <li>
                    <div>Draught:</div>
                    <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.draught)}</div>
                </li>
            {/if}  
            {#if $vessel.enhancedData.characteristics.extended.dimensions.lengthOverall !== null}
            <li>
                <div>Length Overall:</div>
                <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.lengthOverall)}</div>
            </li>
            {/if}    
            {#if $vessel.enhancedData.characteristics.extended.dimensions.airDraught !== null}
            <li>
                <div>Air Draught:</div>
                <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.airDraught)}</div>
            </li>
            {/if}    
            {#if $vessel.enhancedData.characteristics.extended.dimensions.keelToManifold !== null}
            <li>
                <div>Keel to Manifold:</div>
                <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.keelToManifold)}</div>
            </li>
            {/if}    
            {#if $vessel.enhancedData.characteristics.extended.dimensions.depth !== null}
            <li>
                <div>Depth:</div>
                <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.depth)}</div>
            </li>
            {/if}    
            {#if $vessel.enhancedData.characteristics.extended.dimensions.beamMoulded !== null}
            <li>
                <div>Beam Moulded:</div>
                <div>{Vessel.formatDraught($vessel.enhancedData.characteristics.extended.dimensions.beamMoulded)}</div>
            </li>
            {/if}    
            {#if $vessel.enhancedData.characteristics.extended.dimensions.berthCount !== null}
            <li>
                <div>Berth Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.dimensions.berthCount}</div>
            </li>
            {/if}    
            </ul>

            <p class='category'>History:</p>
            <ul>
            {#if $vessel.enhancedData.characteristics.extended.history.vesselNameDate !== null}
            <li>
                <div>Vessel Name Date:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.vesselNameDate}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.commercialOwner !== null}
            <li>
                <div>Commercial Owner:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.commercialOwner}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.builtYear !== null}
            <li>
                <div>Built Year:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.builtYear}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.deadYear !== null}
            <li>
                <div>Dead Year:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.deadYear}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.shipBuilder !== null}
            <li>
                <div>Ship Builder:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.shipBuilder}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.hullNumber !== null}
            <li>
                <div>Hull Number:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.hullNumber}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.registeredOwner !== null}
            <li>
                <div>Registered Owner:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.registeredOwner}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.keelLaidYear !== null}
            <li>
                <div>Keel Laid Year:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.keelLaidYear}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.history.launchYear !== null}
            <li>
                <div>Launch Year:</div>
                <div>{$vessel.enhancedData.characteristics.extended.history.launchYear}</div>
            </li>
            {/if}
            </ul>

            <p class='category'>Propulsion:</p>
            <ul>
            {#if $vessel.enhancedData.characteristics.extended.propulsion.mainEngineCount !== null}
            <li>
                <div>Main Engine Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.mainEngineCount}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.mainEngineDesigner !== null}
            <li>
                <div>Main Engine Designer:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.mainEngineDesigner}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.propulsionType !== null}
            <li>
                <div>Propulsion Type:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.propulsionType}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.engineDesignation !== null}
            <li>
                <div>Engine Designation:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.engineDesignation}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.mcoRpm !== null}
            <li>
                <div>MCO RPM:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.mcoRpm}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.mcoKw !== null}
            <li>
                <div>MCO KW:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.mcoKw}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.mcoHp !== null}
            <li>
                <div>MCO HP:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.mcoHp}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.propellerCount !== null}
            <li>
                <div>Propeller Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.propellerCount}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.propellerType !== null}
            <li>
                <div>Propeller Type:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.propellerType}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.bowThrusterCount !== null}
            <li>
                <div>Bow Thruster Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.bowThrusterCount}</div>
            </li>
            {/if}
            {#if $vessel.enhancedData.characteristics.extended.propulsion.sternThrusterCount !== null}
            <li>
                <div>Stern Thruster Count:</div>
                <div>{$vessel.enhancedData.characteristics.extended.propulsion.sternThrusterCount}</div>
            </li>
            {/if}
            </ul>

            <p class='category'>Registration:</p>
            <ul>
                {#if $vessel.enhancedData.characteristics.extended.registration.class1Code !== null && $vessel.enhancedData.characteristics.extended.registration.class1Code !== ""}
                <li>
                    <div>Class 1 Code:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.class1Code}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.registration.class2Code !== null && $vessel.enhancedData.characteristics.extended.registration.class2Code !== ""}
                <li>
                    <div>Class 2 Code:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.class2Code}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.registration.classDetails !== null}
                <li>
                    <div>Class Details:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.classDetails}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.registration.isIceClassed !== null}
                <li>
                    <div>Is Ice Classed:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.isIceClassed}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.registration.iceClass !== null}
                <li>
                    <div>Ice Class:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.iceClass}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.registration.certificates !== null}
                <li>
                    <div>Certificates:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.registration.certificates}</div>
                </li>
                {/if}
            </ul>
            <p class='category'>Vessel Type and Trading:</p>
            <ul>
                {#if $vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.vesselSubtype !== null}
                <li>
                    <div>Vessel Sub Type:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.vesselSubtype}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.tradingCategoryCode !== null}
                <li>
                    <div>Trading Category Code:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.tradingCategoryCode}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.tradingStatusCode !== null}
                <li>
                    <div>Trading Status Code:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.vesselTypeAndTrading.tradingStatusCode}</div>
                </li>
                {/if}
            </ul>
            <p class='category'>Bunker:</p>
            <ul>
                {#if $vessel.enhancedData.characteristics.extended.bunker.range !== null}
                <li>
                    <div>Range:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.bunker.range}</div>
                </li>
                {/if}
                {#if $vessel.enhancedData.characteristics.extended.bunker.bunkers !== null}
                <li>
                    <div>Bunkers:</div>
                    <div>{$vessel.enhancedData.characteristics.extended.bunker.bunkers}</div>
                </li>
                {/if}
            </ul>
        {/if}
    {/if}
{/if}